import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { allFields } from "@/lib/forms";
import { DraftBanner } from "./FormBadges";

export const AI_NOTICES = {
  assist: "This optional AI action may use approximately $0.01–$0.02 in AI-service credits. AI-generated content may contain errors. Continue?",
  explain: "This optional AI action may use approximately $0.005 in AI-service credits. The explanation provides general educational support and does not change the meaning or requirements of the form. Continue?",
};

// Affirmative confirmation before any AI action runs (never automatic)
export function AiConfirmDialog({ kind, onConfirm, onCancel }) {
  return (
    <Dialog open={!!kind} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent data-testid="ai-confirm-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-[#0A2A5E]">{kind === "explain" ? "Explain This Question in Plain Language" : "Draft with AI"}</DialogTitle>
          <DialogDescription>{AI_NOTICES[kind] || ""}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="ai-cancel-btn">Cancel</Button>
          <Button onClick={onConfirm} data-testid="ai-continue-btn" className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">Continue and Use AI</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// AI draft result: inserted only after the user accepts it
export function AiDraftReview({ draft, label, onAccept, onReject, onRegenerate, busy }) {
  return (
    <Dialog open={!!draft} onOpenChange={(v) => !v && onReject()}>
      <DialogContent data-testid="ai-draft-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-[#0A2A5E]">Review the AI draft</DialogTitle>
          <DialogDescription><span className="font-bold text-[#7C3F00]">{label}</span> — Nothing is inserted until you accept. You can edit the field afterwards.</DialogDescription>
        </DialogHeader>
        <div data-testid="ai-draft-text" className="rounded-lg border border-[#FBD38D] bg-[#FFF7E8] p-3 text-sm text-slate-800 whitespace-pre-wrap max-h-64 overflow-y-auto">{draft?.text}</div>
        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="ghost" onClick={onReject} data-testid="ai-reject-btn">Reject</Button>
          <Button variant="outline" onClick={onRegenerate} disabled={busy} data-testid="ai-regenerate-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Regenerate"}</Button>
          <Button onClick={onAccept} data-testid="ai-accept-btn" className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">Accept and insert</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function val(f, v) {
  if (v in { "": 1 } || v == null || v === false) return "—";
  if (f.type === "checkbox") return "Yes";
  if (f.type === "rating") return `${v} / 5`;
  return String(v);
}

// Pre-submission review: exactly what will be sent, to whom, with affirmative confirmation
export function SubmitReviewDialog({ open, onOpenChange, template, answers, recipients, confirmed, setConfirmed, onSubmit, busy }) {
  const names = recipients?.recipients?.map((r) => r.name) || [];
  const available = !!recipients?.available;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="submit-review-dialog" className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-[#0A2A5E]">Review before submitting</DialogTitle>
          <DialogDescription>Check exactly what will be submitted. You can still go back and edit, or cancel.</DialogDescription>
        </DialogHeader>
        {template.banner && <DraftBanner text={template.banner} status={template.status} compact />}
        {available ? (
          <p data-testid="submit-recipient-statement" className="rounded-lg border border-slate-200 bg-[#F5F9FE] p-3 text-sm text-slate-800">
            You are about to send this form to the following HR Managers of {recipients.org_name}: <span className="font-semibold">{names.join(", ")}</span>. Once submitted, it may become part of an organizational record and may be handled according to the organization's policies and applicable requirements.
          </p>
        ) : (
          <p role="alert" data-testid="no-recipient-notice" className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{recipients?.notice}</p>
        )}
        <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 text-sm" data-testid="submit-review-answers">
          {template.sections.map((s) => (
            <div key={s.title} className="p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#C7761A] font-bold mb-1">{s.title}</div>
              <dl className="grid sm:grid-cols-3 gap-y-1 gap-x-3">
                {s.fields.map((f) => <div key={f.id} className="contents"><dt className="text-slate-500">{f.label}</dt><dd className="sm:col-span-2 text-slate-800 whitespace-pre-wrap">{val(f, answers[f.id])}</dd></div>)}
              </dl>
            </div>
          ))}
        </div>
        {template.disclaimer && <p data-testid="submit-review-disclaimer" className="text-[11px] text-slate-500 leading-relaxed">{template.disclaimer}</p>}
        {available && (
          <label className="flex items-start gap-2 text-sm text-slate-800 cursor-pointer">
            <Checkbox data-testid="submit-confirm-checkbox" checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} className="mt-0.5" />
            <span>I have reviewed the information above and confirm that I want to submit it to the HR Managers listed.</span>
          </label>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="submit-review-cancel">Go back and edit</Button>
          <Button onClick={onSubmit} disabled={!available || !confirmed || busy} data-testid="submit-review-confirm" className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit to HR Managers"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function missingRequired(template, answers) {
  return allFields(template).filter((f) => f.required && !answers[f.id]);
}
